import {
  AutocompleteInput,
  DateInput,
  Edit,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  DeleteButton,
} from "react-admin";
import { Grid, Typography } from "@mui/material";
import { statesList } from "../../../lib/constants";
import { useAuth } from "../../../hooks/useAuth";
import StateCheckboxArrayInput from "../StateCheckboxArrayInput";
import { AgentTitle } from "./index";

const EditToolbar = ({ showDelete }) => (
  <Toolbar>
    <SaveButton />
    {showDelete && <DeleteButton />}
  </Toolbar>
);

// #7/#3 — Applied GA admins and Lite Admins (#73) can re-assign an agent's agency
// and edit identity here. NPN is the agent's identity key, shown read-only for
// context. #9/#10 — agency principals also reach this view (for agents in their
// hierarchy) but may ONLY edit licensed states; identity/agency are locked for
// them (the backend enforces this too). Delete stays full-admin only.
export const AgentEdit = () => {
  const { user, isFullAdmin, isLiteAdmin, canDelete } = useAuth();
  const lockIdentity = !(isFullAdmin(user) || isLiteAdmin(user));
  return (
    <Edit title={<AgentTitle />}>
      <SimpleForm toolbar={<EditToolbar showDelete={canDelete(user)} />}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Agent</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextInput source="npn" label="NPN" fullWidth disabled />
          </Grid>
          <Grid item xs={6}>
            <ReferenceInput source="agency" reference="agency">
              <AutocompleteInput
                fullWidth
                optionText="name"
                label="Agency"
                disabled={lockIdentity}
              />
            </ReferenceInput>
          </Grid>
          <Grid item xs={6}>
            <TextInput source="firstName" fullWidth disabled={lockIdentity} />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="lastName" fullWidth disabled={lockIdentity} />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="email" fullWidth disabled={lockIdentity} />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="phone" fullWidth disabled={lockIdentity} />
          </Grid>
          {/* #89 - SSN and DOB, importable from the upload sheet and editable
              here. Rendered only for Applied GA admins; principals reaching
              this view must not receive the SSN at all. */}
          {!lockIdentity && (
            <>
              <Grid item xs={6}>
                <TextInput source="ssn" label="SSN" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <DateInput source="dob" label="Date of birth" fullWidth />
              </Grid>
              {/* #105 - staff override of the system's new-vs-established call.
                  Left unset, the system decides (imported agents are
                  established; a first submitted request also marks it). "New
                  agent" makes their next request sign the new-agent DocuSign
                  and open a New Agent ticket; "Established" skips both. */}
              <Grid item xs={6}>
                <SelectInput
                  source="declaredRequestType"
                  label="Agent standing"
                  fullWidth
                  choices={[
                    { id: "new-agent", name: "New agent" },
                    { id: "existing-agent", name: "Established agent" },
                  ]}
                  emptyText="Decided by the system"
                  format={(v) => v ?? ""}
                  parse={(v) => (v === "" ? null : v)}
                  helperText="Controls whether their next request runs the new-agent signing and ticket"
                />
              </Grid>
            </>
          )}
          {/* #22 — cache of the agent's most recent FSD choice (pre-selects the
              "who recommended you?" dropdown on their next request). */}
          <Grid item xs={6}>
            <TextInput
              source="fsd"
              label="FSD (most recent choice)"
              fullWidth
              disabled={lockIdentity}
            />
          </Grid>
          {/* #9/#10 — the states this agent is licensed in. Agents self-declare
              this once in the flow; Applied GA admins and the agent's agency
              principal can correct it here. Drives which carriers the agent
              sees. */}
          <Grid item xs={12}>
            <StateCheckboxArrayInput
              source="licensedStates"
              label="Licensed states"
              choices={statesList}
              fullWidth
            />
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};

export default AgentEdit;
